import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  FileText,
  Calendar,
  TrendingUp,
  RefreshCw,
  Copy,
  Download,
  Search,
  Filter,
  ChevronRight,
  BookOpen,
  AlertCircle,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { formatCurrency } from '../lib/utils';
import { SafeHTML } from '../lib/sanitize';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NarrativeReportsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency } = useApp();
  
  const [scenarios, setScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentNarrative, setCurrentNarrative] = useState(null);

  // Fetch saved scenarios
  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const response = await api.get(`/scenarios`);
        setScenarios(response.data);
      } catch (error) {
        logger.error('Error fetching scenarios:', error);
        toast.error('Failed to load saved scenarios');
      } finally {
        setIsLoading(false);
      }
    };
    fetchScenarios();
  }, []);

  // Filter scenarios
  const filteredScenarios = scenarios.filter(scenario => {
    const matchesSearch = scenario.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          scenario.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === 'all' ||
                          (filterType === 'with-narrative' && scenario.narrative) ||
                          (filterType === 'without-narrative' && !scenario.narrative);
    
    return matchesSearch && matchesFilter;
  });

  // Generate narrative for selected scenario
  const handleGenerateNarrative = async (scenario) => {
    setIsGenerating(true);
    try {
      const response = await api.post(`/generate-narrative`, {
        lei_measure_name: scenario.configuration?.lei_measure_name || 'Learning Initiative',
        kpi_name: scenario.configuration?.kpi_names?.join(', ') || 'Business KPIs',
        results: scenario.results,
        configuration: scenario.configuration,
        analysis_type: scenario.analysis_type || 'single',
        include_sections: true,
      });
      
      // Update scenario with new narrative
      const updatedNarrative = response.data.narrative;
      setCurrentNarrative(updatedNarrative);
      
      // Optionally save the narrative back to the scenario
      try {
        await api.patch(`/scenarios/${scenario.id}`, {
          narrative: updatedNarrative
        });
        
        // Update local state
        setScenarios(prev => prev.map(s => 
          s.id === scenario.id ? { ...s, narrative: updatedNarrative } : s
        ));
        
        toast.success('Narrative generated and saved');
      } catch (saveError) {
        logger.error('Could not save narrative to scenario:', saveError);
        toast.success('Narrative generated (not saved to scenario)');
      }
    } catch (error) {
      logger.error('Narrative generation error:', error);
      toast.error('Failed to generate narrative');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyNarrative = () => {
    const narrativeText = currentNarrative || selectedScenario?.narrative;
    if (narrativeText) {
      const plainText = narrativeText.replace(/<[^>]*>/g, '').replace(/\n\n+/g, '\n\n');
      navigator.clipboard.writeText(plainText);
      toast.success('Narrative copied to clipboard');
    }
  };

  const handleSelectScenario = (scenario) => {
    setSelectedScenario(scenario);
    setCurrentNarrative(scenario.narrative || null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get analysis type label
  const getAnalysisTypeLabel = (type) => {
    switch (type) {
      case 'single': return 'Single Measure';
      case 'portfolio': return 'Portfolio';
      case 'matrix': return 'Full Matrix';
      default: return 'Analysis';
    }
  };

  // Get analysis type color
  const getAnalysisTypeColor = (type) => {
    switch (type) {
      case 'single': return 'bg-teal-100 text-teal-700';
      case 'portfolio': return 'bg-blue-100 text-blue-700';
      case 'matrix': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Parse narrative into sections
  const parseNarrativeSections = (text) => {
    if (!text) return null;
    
    const sections = {
      executive: '',
      methodology: '',
      recommendations: '',
      risks: ''
    };
    
    const execMatch = text.match(/(?:#+\s*)?(?:EXECUTIVE SUMMARY|Executive Summary)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:METHODOLOGY|TECHNICAL|CAUSAL|RISK|RECOMMENDATION|$))/i);
    const methodMatch = text.match(/(?:#+\s*)?(?:METHODOLOGY|TECHNICAL BREAKDOWN|CAUSAL COMPONENTS|TECHNICAL)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:RISK|RECOMMENDATION|STRATEGIC|$))/i);
    const riskMatch = text.match(/(?:#+\s*)?(?:RISK|RISKS|RISK ANALYSIS|RISK CONSIDERATIONS)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:RECOMMENDATION|STRATEGIC|NEXT STEPS|$))/i);
    const recoMatch = text.match(/(?:#+\s*)?(?:RECOMMENDATION|STRATEGIC|NEXT STEPS|STRATEGIC RECOMMENDATIONS)[:\s]*([\s\S]*?)$/i);
    
    if (execMatch) sections.executive = cleanSectionText(execMatch[1]);
    if (methodMatch) sections.methodology = cleanSectionText(methodMatch[1]);
    if (riskMatch) sections.risks = cleanSectionText(riskMatch[1]);
    if (recoMatch) sections.recommendations = cleanSectionText(recoMatch[1]);
    
    if (!sections.executive && !sections.methodology) {
      sections.executive = cleanSectionText(text);
    }
    
    return sections;
  };

  // Clean up section text by removing markdown artifacts
  const cleanSectionText = (text) => {
    if (!text) return '';
    return text
      // Remove markdown headers (##, ###, etc.)
      .replace(/^#+\s*/gm, '')
      // Remove standalone ## at start or end
      .replace(/^\s*##\s*$/gm, '')
      // Remove section headers that might be duplicated
      .replace(/^(?:EXECUTIVE SUMMARY|TECHNICAL BREAKDOWN|METHODOLOGY|RISK ANALYSIS|RISK CONSIDERATIONS|STRATEGIC RECOMMENDATIONS|RECOMMENDATIONS)[:\s]*/gim, '')
      // Remove lines that are just single characters (artifacts)
      .replace(/^\s*[a-z]\s*$/gm, '')
      // Clean up extra whitespace
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const formatText = (text) => {
    if (!text) return '';
    return text
      // Remove any remaining markdown headers
      .replace(/^#+\s*(.*)$/gm, '$1')
      // Remove standalone ## or ###
      .replace(/^\s*#{1,6}\s*$/gm, '')
      // Convert **text** to bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert *text* to italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Convert newlines to <br />
      .replace(/\n/g, '<br />');
  };

  const narrativeToDisplay = currentNarrative || selectedScenario?.narrative;
  const sections = narrativeToDisplay ? parseNarrativeSections(narrativeToDisplay) : null;

  const __pageTheme = getLightSectionTheme('Insights');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`Narrative Reports`} description={`AI-assisted narrative reports for your data.`} theme={__pageTheme} />
    )}>
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
              </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800">
            <strong>How it works:</strong> Select a saved scenario from the list below to view or generate its narrative. 
            Narratives are only available for calculations you've explicitly saved using the "Save Scenario" button in each calculator.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scenarios List */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Saved Scenarios</CardTitle>
              <CardDescription>
                {filteredScenarios.length} scenario{filteredScenarios.length !== 1 ? 's' : ''} available
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search and Filter */}
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search scenarios..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger>
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by narrative status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scenarios</SelectItem>
                    <SelectItem value="with-narrative">With Narrative</SelectItem>
                    <SelectItem value="without-narrative">Without Narrative</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Scenarios List */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                  </div>
                ) : filteredScenarios.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No scenarios found</p>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => navigate('/configure')}
                      className="mt-2"
                    >
                      Run a calculation <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                ) : (
                  filteredScenarios.map((scenario) => (
                    <div
                      key={scenario.id}
                      onClick={() => handleSelectScenario(scenario)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedScenario?.id === scenario.id
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-slate-200 hover:border-purple-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {scenario.name || 'Untitled Scenario'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-[10px] ${getAnalysisTypeColor(scenario.analysis_type)}`}>
                              {getAnalysisTypeLabel(scenario.analysis_type)}
                            </Badge>
                            {scenario.narrative && (
                              <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600">
                                <Sparkles className="h-2.5 w-2.5 mr-1" />
                                Narrative
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(scenario.created_at)}
                          </p>
                        </div>
                        <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-colors ${
                          selectedScenario?.id === scenario.id ? 'text-purple-500' : 'text-slate-300'
                        }`} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Narrative Display */}
        <div className="lg:col-span-2">
          {selectedScenario ? (
            <Card className="border-purple-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                      {selectedScenario.name || 'Untitled Scenario'}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {getAnalysisTypeLabel(selectedScenario.analysis_type)} • 
                      ROI: {((selectedScenario.results?.roi_percent || selectedScenario.results?.total_roi_percent || 0) / 100).toFixed(1)}x • 
                      Impact: {formatCurrency(selectedScenario.results?.total_financial_impact || 0, currency)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {narrativeToDisplay && (
                      <Button variant="outline" size="sm" onClick={handleCopyNarrative}>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleGenerateNarrative(selectedScenario)}
                      disabled={isGenerating}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-1" />
                          {narrativeToDisplay ? 'Regenerate' : 'Generate'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {narrativeToDisplay ? (
                  <div className="space-y-4">
                    {/* Executive Summary */}
                    {sections?.executive && (
                      <div className="p-4 bg-purple-50/50 rounded-lg border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-purple-600" />
                          <h4 className="font-semibold text-slate-800">Executive Summary</h4>
                        </div>
                        <SafeHTML 
                          className="text-sm text-slate-600 prose prose-sm max-w-none"
                          html={formatText(sections.executive)}
                        />
                      </div>
                    )}

                    {/* Technical Breakdown */}
                    {sections?.methodology && (
                      <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-4 w-4 text-blue-600" />
                          <h4 className="font-semibold text-slate-800">Technical Breakdown</h4>
                        </div>
                        <SafeHTML 
                          className="text-sm text-slate-600 prose prose-sm max-w-none"
                          html={formatText(sections.methodology)}
                        />
                      </div>
                    )}

                    {/* Risk Analysis */}
                    {sections?.risks && (
                      <div className="p-4 bg-amber-50/50 rounded-lg border border-amber-100">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                          <h4 className="font-semibold text-slate-800">Risk Analysis</h4>
                        </div>
                        <SafeHTML 
                          className="text-sm text-slate-600 prose prose-sm max-w-none"
                          html={formatText(sections.risks)}
                        />
                      </div>
                    )}

                    {/* Recommendations */}
                    {sections?.recommendations && (
                      <div className="p-4 bg-emerald-50/50 rounded-lg border border-emerald-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="h-4 w-4 text-emerald-600" />
                          <h4 className="font-semibold text-slate-800">Strategic Recommendations</h4>
                        </div>
                        <SafeHTML 
                          className="text-sm text-slate-600 prose prose-sm max-w-none"
                          html={formatText(sections.recommendations)}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="h-16 w-16 text-purple-700 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-800 mb-2">
                      No Narrative Yet
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">
                      This scenario doesn't have a narrative. Click "Generate" to create 
                      an AI-powered analysis with executive summary and technical breakdown.
                    </p>
                    <Button
                      onClick={() => handleGenerateNarrative(selectedScenario)}
                      disabled={isGenerating}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Generate Narrative
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-16">
                <div className="text-center">
                  <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-800 mb-2">
                    Select a Scenario
                  </h3>
                  <p className="text-sm text-slate-500 max-w-md mx-auto">
                    Choose a saved scenario from the list to view or generate its narrative report.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
    </PageShell>
  );
};

export default NarrativeReportsPage;
